//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { UseFormReturn } from 'react-hook-form';

import {
  CampaignRequestsSortCriteria,
  SortDirection,
} from '@apis/campaign-requests/creators/CreatorsCampaignRequestApiModel';
import { SortDir } from '@apis/campaign-requests/shared/Sorting';
import { ContentLibrarySortType } from '@apis/content-library/contentLibraryApiModel';
import {
  ContentRightsSources,
  CreatorPortalProfile,
  CreatorsCampaignInfluencerCommissionTypeEnum,
  CreatorsCampaignsWorkflowResponse,
} from '@apis/creators-campaigns/CreatorsCampaignsApiModel';
import { ICampaignAnalyticsStatsRequest } from '@apis/metrics/metricsApiModel';
import {
  ExchangeRate,
  PaymentsPayoutMethodsType,
  PaymentsTransactionStatusType,
  RecipientStatus,
} from '@apis/payments/paymentsApiModel';
import { ShopifyStoreInfoResponse } from '@apis/shopify/ShopifyApiModel';
import { DEFAULT_ALL_SELECTION } from '@campaigns/components/campaign-requests/creator-campaigns/CreatorsCampaignDetailsConstants';
import { IAliasedDateRange } from '@components/date-range-picker/dateRangePickerModels';
import { ProfileNetwork } from '@influencers/constants/influencersConstants';
import { SocialCreatorsPlatforms } from '@models/CreatorsTypes';
import ItemId from '@models/ItemId';
import { UniversalItemType } from '@models/UniversalItemType';
import { navigateToState } from '@react-bridge/wrappers/navigation';
import { CustomReportId } from '@reporting/custom-reporting/types/CustomReportId';

import { RoutableStateName } from 'app/top-level-states';

export interface CreatorsCampaignsParams {
  campaignSlugName?: string; // TODO: Check if there's any chance this could be undefined and why
  fromReportId?: CustomReportId;
}

export interface CreatorNotesParams {
  creatorUsername?: string;
  network?: SocialCreatorsPlatforms;
}

export interface CreatorsCampaignsCreatorDetailsParams {
  campaignSlugName: string;
  creatorUsername: string;
  network?: ProfileNetwork;
}

export interface CreatorsCampaignsAffiliateOrderDetailsParams {
  campaignSlugName?: string;
  creatorUsername?: string;
  affiliateCode?: string;
  network?: ProfileNetwork;
}

export interface CreatorsCampaignsRequestSparkCodeParams {
  campaignSlugName?: string;
  itemId?: ItemId;
  itemType?: UniversalItemType;
}

export interface CreatorsCampaignsApplySparkCodeParams {
  campaignSlugName?: string;
  itemId?: ItemId;
  itemType?: UniversalItemType;
  sparkCode?: string;
}

export enum CampaignsCreatorsSortKey {
  Recent = 'Recent',
  Content = 'Content',
  Impressions = 'Impressions',
  Engagement = 'Engagement',
  EMV = 'EMV',
  Visits = 'Visits',
  AffiliateRevenue = 'AffiliateRevenue',
  OnsiteRevenue = 'OnsiteRevenue',
}

export interface CampaignsCreatorsSortObject {
  sortKey: CampaignsCreatorsSortKey;
  order: SortDir;
}
export type PlatformFilter =
  | SocialCreatorsPlatforms
  | typeof DEFAULT_ALL_SELECTION;
export interface SearchFilterSortPagination<
  T extends
    | CampaignsCreatorsSortObject
    | CreatorsCampaignContentsSortObject
    | CreatorsCampaignAffiliatesSortObject
    | CreatorsCampaignsProposalsSortObject,
> {
  platformFilter: PlatformFilter;
  searchTerm?: string;
  sortBy: T;
  pageSize: number;
  pageNumber: number;
}

export interface CreatorsCampaignsProposalsSearch {
  searchTerm?: string;
}

export interface CreatorsCampaignsPagination {
  pageSize: number;
  pageNumber: number;
}

export interface CreatorsCampaignsProposalsListsQueryRequest
  extends CreatorsCampaignsProposalsSearch,
    CreatorsCampaignsPagination {
  campaignSlugName: string | undefined;
}

export enum CreatorsCampaignsPaymentsSortKey {
  FixedPay = 'fixedPayValue',
  Commission = 'commissionValue',
  MediaCount = 'mediaCount',
  TotalSentAmount = 'totalSentAmount',
  TotalPending = 'totalPending',
}

export enum CreatorsCampaignsProposalsSortKey {
  ReceivedOn = 'receivedOn',
}

export enum CreatorsCampaignsProposalsColumnKey {
  Creators = 'Creators',
  ReceivedOn = 'ReceivedOn',
  ShippingLocation = 'ShippingLocation',
  Status = 'Status',
  OrderStatus = 'OrderStatus',
  Action = 'Action',
}

export enum CreatorsCampaignsOrdersSortKey {
  CreatedOn = 'createdOn',
}

export enum CreatorsCampaignsOrdersColumnKey {
  Creators = 'Creators',
  FulfillmentStatus = 'FulfillmentStatus',
  ShipmentStatus = 'ShipmentStatus',
  OrderNumber = 'OrderNumber',
  CreatedOn = 'CreatedOn',
  Action = 'Action',
}

export interface CreatorsCampaignsProposalsSortObject {
  sortKey: CreatorsCampaignsProposalsSortKey;
  order: SortDirection;
}

export interface CreatorMinimalInfoInterface {
  username: string;
  fullName?: string;
  platform: SocialCreatorsPlatforms;
  profilePicture?: string;
}

export interface BasicCreatorsInfo {
  username: string;
  network: SocialCreatorsPlatforms;
}

export interface CreatorsCampaignsProposalDetailsParams {
  campaignSlugName: string;
  creatorUsername: string;
  network?: ProfileNetwork;
}

export enum CreatorsCampaignListSortKey {
  Recent = 'CreatedAt',
  Creators = 'CreatorsCount',
  AffiliateRevenue = 'TotalRevenue',
  Visits = 'Clicks',
  OnsiteRevenue = 'OnSiteRevenue',
}

export interface CreatorsCampaignListSortObject {
  sortKey: CreatorsCampaignListSortKey;
  order: SortDirection;
}

export interface CreatorsCampaignRequestListSortObject {
  sortKey: CampaignRequestsSortCriteria;
  order: SortDirection;
}

export interface CreatorsRequestListSearchFilterSortPagination {
  searchTerm?: string;
  sortBy: CreatorsCampaignRequestListSortObject;
  pageSize: number;
  pageNumber: number;
}

export interface CreatorsListSearchFilterSortPagination {
  searchTerm?: string;
  sortBy: CreatorsCampaignListSortObject;
  pageSize: number;
  pageNumber: number;
  dateRange?: IAliasedDateRange;
}

export interface CreatorsCampaignContentsSortObject {
  sortKey: ContentLibrarySortType;
  order: SortDirection;
}

export interface UpdateCampaignBasicsFormValues {
  campaignImageUrl?: string;
  description?: string;
  title?: string;
}

export interface ShopifyAffiliateConfigurationFormValues {
  pricingRuleId?: string;
}

export interface GoogleAnalyticsAffiliateConfigurationFormValues {
  destinationUrl?: string;
}

export interface ImpactNamedIdNameFormValues {
  id?: string;
  name?: string;
}

export interface ImpactAffiliateConfigurationFormValues {
  program?: ImpactNamedIdNameFormValues;
  deal?: ImpactNamedIdNameFormValues;
  term?: ImpactNamedIdNameFormValues;
}

export interface InfluencersCommissionFormValues {
  commissionType?: CreatorsCampaignInfluencerCommissionTypeEnum;
  commissionValuePercentage?: number;
  commissionValueFixed?: number;
  currency?: string;
}

export interface AffiliateConfigurationFormValues {
  enabled?: boolean;
  affiliatePartner?: string;
  shopify?: ShopifyAffiliateConfigurationFormValues;
  googleAnalytics?: GoogleAnalyticsAffiliateConfigurationFormValues;
  impact?: ImpactAffiliateConfigurationFormValues;
  influencersCommission?: InfluencersCommissionFormValues;
}

export interface UpdateCampaignPaymentFormValues {
  fixedPay?: {
    collectPaymentDetails?: boolean;
    currency?: string;
    enabled?: boolean;
    proposeCostEnabled?: boolean;
    value?: number;
  };
  affiliateConfiguration?: AffiliateConfigurationFormValues;
  isCollectPaymentDetailsEnabled?: boolean;
}

export interface CampaignPlatformTask {
  title: string;
  guidance: string;
}

export interface CampaignInstagramTask extends CampaignPlatformTask {
  shareAdCode?: boolean;
}

export interface CampaignWorkflowPlatformsTasks {
  instagramTasks?: {
    post?: CampaignInstagramTask;
    reel?: CampaignInstagramTask;
    story?: CampaignInstagramTask;
  };
  tiktokTasks?: {
    brandName?: string;
    videos?: CampaignPlatformTask[];
    sparkAdsDuration?: string;
  };
  youtubeTasks?: {
    videos?: CampaignPlatformTask[];
  };
  assets?: { url: string; file?: File }[];
}

export interface CampaignWorkflowCreatorsTasks {
  enabled?: boolean;
  contentReviewEnabled?: boolean;
  tasks?: CampaignWorkflowPlatformsTasks;
}

export interface UpdateCampaignContentRightsFormValues {
  description?: string;
  expiryDate?: string;
  isEnabled?: boolean;
  source?: ContentRightsSources;
}

export type CampaignConfigurationFormNavigationTriggerSourceType =
  | 'bottom-navigation'
  | 'header';

export interface CampaignConfigurationFormNavigationState<
  T extends RoutableStateName = RoutableStateName,
> {
  stateArgs?: Parameters<typeof navigateToState<T>>;
  navigationTriggerSource: CampaignConfigurationFormNavigationTriggerSourceType;
  shouldShowToast?: boolean;
}

export interface CreatorCampaignsProductVariantFormValues {
  description?: string;
  id?: string;
  image?: string;
  price?: string;
  quantity?: string;
}

export interface CreatorCampaignsRequestProductDetailsFormValues {
  brand?: string;
  category?: string;
  description?: string;
  imageFile?: File;
  imagePreviewUrl?: string;
  name?: string;
  pdpUrl?: string;
  price?: string;
  displayProductId?: string;
  quantity?: string;
  variants?: CreatorCampaignsProductVariantFormValues[];
}

export interface CreatorsCampaignAffiliatesSortObject {
  sortKey?: CreatorsCampaignAffiliateSortKey;
  order?: SortDir;
}

export enum CreatorsCampaignAffiliateSortKey {
  EcommerceRevenue = 'ecommerceTotalRevenueValue',
  EcommerceOrders = 'ecommerceOrdersCount',
  EcommerceCommission = 'ecommerceTotalCommissionValue',
  FixedPay = 'fixedPayValue',
  UnifiedMetricsVisits = 'unifiedMetricsVisits',
  GoogleAnalyticsRevenue = 'googleAnalyticsRevenue',
  UnifiedMetricsOrders = 'unifiedMetricsTransactions',
}

export interface CampaignAnalyticsStatsRequest
  extends Omit<ICampaignAnalyticsStatsRequest, 'interval'> {
  startDate?: Date;
  endDate?: Date;
}

export enum CreatorPaymentMethod {
  Offline = 'Offline',
  Online = 'Online',
}

export interface CreatorBasicInfo {
  creatorId?: string;
  email?: string;
  network?: SocialCreatorsPlatforms;
  username?: string;
  profilePictureUrl?: string;
  name?: string;
  creatorProfileId?: string;
}

export interface CreatorUniqueIdentifier {
  creatorId?: string;
  network?: SocialCreatorsPlatforms;
}

export interface MonetaryValue {
  amount?: number;
  currency?: string;
  exchangeRate?: ExchangeRate;
}

export interface CreatorCampaignPayoutBreakdown {
  commission?: MonetaryValue;
  fixedPay?: MonetaryValue;
  paid?: MonetaryValue;
  pending?: MonetaryValue;
  sent?: MonetaryValue;
}

export interface CreatorsCampaignsPaymentCreator {
  name?: string;
  username?: string;
  creatorId?: string;
  email?: string;
  network?: SocialCreatorsPlatforms;
  profilePictureUrl?: string;
  mediaCount?: number;
  recipientStatus?: RecipientStatus;
  creatorPortalProfile?: CreatorPortalProfile;
  metadata?: {
    trolleyComplianceStatus?: string;
    trolleyTaxStatus?: string;
  };
  fixedPayValue?: number;
  fixedPayCurrency?: string;
  fixedPayExchangeRate?: ExchangeRate;
  commissionValue?: number;
  commissionCurrency?: string;
  commissionExchangeRate?: ExchangeRate;
  totalSentAmount?: number;
  sentCurrency?: string;
  sentExchangeRate?: ExchangeRate;
  totalPending?: number;
  pendingCurrency?: string;
}

export type PaymentModalState =
  | 'PAYMENT'
  | 'REVIEW'
  | 'NOTIFY'
  | 'SUCCESS'
  | 'PAYMENT_ERROR'
  | 'EMAIL_PREVIEW'
  | 'EMAIL_SENT';

export interface CreatorsCampaignsPaymentHistoryFilters {
  searchTerm: string;
  networks: SocialCreatorsPlatforms[] | typeof DEFAULT_ALL_SELECTION;
  dateRange: IAliasedDateRange;
  payoutMethods: PaymentsPayoutMethodsType[] | typeof DEFAULT_ALL_SELECTION;
  statuses: PaymentsTransactionStatusType[] | typeof DEFAULT_ALL_SELECTION;
}

export interface ShopifyConfigurationProps {
  shopifyPricingRulesOptions?: { label: string; value: string }[];
  shopifyStoreInfo?: ShopifyStoreInfoResponse;
}

export interface ImpactConfigurationProps {
  linkedImpactProgramId?: string;
}

export interface BaseAffiliatePartnerProps {
  hasReservedGAParam?: boolean;
  isEcomEnabled?: boolean;
  isGAConnected?: boolean;
  isImpactConnected?: boolean;
  linkedImpactProgramId?: string;
  shopifyStoreInfo?: ShopifyStoreInfoResponse;
}

export interface AffiliatePartnerDropdownProps
  extends BaseAffiliatePartnerProps {
  isGoogleAnalyticsConfigured?: boolean;
  isShopifyConfigured?: boolean;
}

export interface AffiliatePartnerConfigurationProps
  extends BaseAffiliatePartnerProps {
  campaignSlugName?: string;
  formMethods: UseFormReturn<UpdateCampaignPaymentFormValues>;
  paymentDetails?: {
    googleAnalyticsConfiguration?: CreatorsCampaignsWorkflowResponse['googleAnalyticsConfiguration'];
    influencersCommission?: CreatorsCampaignsWorkflowResponse['influencersCommission'];
    storeConfigurations?: CreatorsCampaignsWorkflowResponse['storeConfigurations'];
  };
  shopifyPricingRulesOptions?: { label: string; value: string }[];
}
